import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../providers/AuthProvider'

const formatPhone = (p) => {
  if (!p) return '-'
  let clean = p.replace(/[^0-9]/g, '')
  if (clean.startsWith('82')) clean = '0' + clean.slice(2)
  if (clean.length === 11) return clean.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
  if (clean.length === 10) return clean.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
  return p
}


export default function MyPagePharmacy() {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [pharmacyProfile, setPharmacyProfile] = useState(null)
  const [myJobs, setMyJobs] = useState([])
  const [myApplications, setMyApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')


  useEffect(() => {
    loadMyPage()
  }, [])

  async function loadMyPage() {
    setLoading(true)
    setError('')
    try {
      // ✅ 로그인 사용자 정보
      const {
        data: { user }
      } = await supabase.auth.getUser()
      if (!user) {
        setUser(null)
        setLoading(false)
        return
      }
      setUser(user)

      // ✅ 기본 프로필
      const { data: prof } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle()
      setProfile(prof || null)

      // ✅ 약국 상세 정보 (있을 수도, 없을 수도 있음)
      const { data: pharmProf } = await supabase
        .from('pharmacy_profiles')
        .select('*')
        .eq('pharmacy_id', user.id)
        .maybeSingle()
      setPharmacyProfile(pharmProf || null)

      // ✅ 내가 올린 공고
      const { data: jobs } = await supabase
        .from('job_posts')
        .select('*')
        .eq('created_by', user.id)
        .order('created_at', { ascending: false })
      const jobList = jobs || []
      setMyJobs(jobList)

      // ✅ 내 공고에 들어온 지원자 목록
      if (jobList.length > 0) {
        const jobIds = jobList.map((j) => j.id)
        const { data: apps } = await supabase
          .from('applications')
          .select(
            `
            id,
            job_id,
            pharmacist_id,
            pharmacist_name,
            contact,
            request_message,
            status,
            created_at,
            job_posts:job_id (
              title,
              date,
              time,
              region,
              district,
              location
            )
          `
          )
          .in('job_id', jobIds)
          .order('created_at', { ascending: false })

        setMyApplications(apps || [])
      } else {
        setMyApplications([])
      }
    } catch (e) {
      console.error(e)
      setError('마이페이지 정보를 불러오는 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }



  if (!user) {
    return (
      <div className="max-w-4xl mx-auto">
        <h2 className="text-xl font-bold mb-2">약국 마이페이지</h2>
        <p className="text-sm text-gray-600">로그인이 필요합니다.</p>
      </div>
    )
  }

  // ✅ 주소 변경 로직
  const { refreshProfile } = useAuth()

  const loadDaumPostcode = (callback) => {
    const existing = document.getElementById('daum-postcode-script')
    if (!existing) {
      const script = document.createElement('script')
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
      script.id = 'daum-postcode-script'
      script.onload = callback
      document.body.appendChild(script)
    } else {
      callback()
    }
  }

  const handleUpdateAddress = () => {
    loadDaumPostcode(() => {
      new window.daum.Postcode({
        oncomplete: (data) => {
          let fullAddress = data.address
          let extra = ''
          if (data.addressType === 'R') {
            if (data.bname !== '') extra += data.bname
            if (data.buildingName !== '') extra += extra !== '' ? ', ' + data.buildingName : data.buildingName
            fullAddress += extra !== '' ? ` (${extra})` : ''
          }

          // ✅ 상세 주소 입력 받기
          const detail = window.prompt('상세 주소를 입력해주세요 (예: 101동 101호):', '')
          if (detail) {
            fullAddress = `${fullAddress} ${detail}`
          }

          if (window.kakao && window.kakao.maps && window.kakao.maps.services) {
            const geocoder = new window.kakao.maps.services.Geocoder()
            geocoder.addressSearch(data.address, async (result, status) => {
              if (status === window.kakao.maps.services.Status.OK) {
                const lat = parseFloat(result[0].y)
                const lng = parseFloat(result[0].x)

                try {
                  const { error: updateErr, count } = await supabase
                    .from('profiles')
                    .update({
                      address: fullAddress,
                      latitude: lat,
                      longitude: lng
                    }, { count: 'exact' })
                    .eq('user_id', user.id)

                  if (updateErr) throw updateErr

                  if (count === 0) {
                    // Attempt upsert fallback
                    const { error: upsertErr } = await supabase.from('profiles').upsert({
                      user_id: user.id,
                      address: fullAddress,
                      latitude: lat,
                      longitude: lng,
                      role: 'pharmacy',
                      approval_status: 'pending'
                    })
                    if (upsertErr) throw upsertErr
                  }

                  alert('주소가 성공적으로 변경되었습니다.')
                  if (refreshProfile) refreshProfile()
                  loadMyPage()
                } catch (e) {
                  console.error(e)
                  alert('주소 변경 중 오류가 발생했습니다.')
                }
              } else {
                alert('주소의 위치 정보를 찾을 수 없습니다.')
              }
            })
          } else {
            alert('지도 서비스를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.')
          }
        },
      }).open()
    })
  }

  return (
    <div className="max-w-5xl mx-auto grid gap-6">
      <h2 className="text-xl font-bold">약국 마이페이지</h2>

      {loading && <p className="text-sm text-gray-500">불러오는 중...</p>}
      {error && (
        <div className="text-sm text-red-600 border border-red-300 bg-red-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {/* ✅ 내 정보 */}
      <section className="border rounded-xl p-5 bg-white grid gap-2">
        <h3 className="font-semibold text-base">내 정보</h3>
        {/* 이메일 숨김 */}
        <p className="text-sm text-gray-700">
          <strong>대표자 이름:</strong>{' '}
          {pharmacyProfile?.owner_name || profile?.display_name || '-'}
        </p>
        <p className="text-sm text-gray-700">
          <strong>연락처:</strong> {formatPhone(pharmacyProfile?.phone || profile?.phone)}
        </p>
        <div className="text-sm text-gray-700 flex items-center gap-2">
          <strong>주소:</strong>
          <span>{profile?.address || '-'}</span>
          <button
            onClick={handleUpdateAddress}
            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-1 rounded border transition"
          >
            주소 변경
          </button>
        </div>
        <p className="text-sm text-gray-700">
          <strong>역할:</strong> {profile?.role || 'pharmacy'}
        </p>
      </section>

      {/* ✅ 내가 등록한 공고 */}
      <section className="border rounded-xl p-5 bg-white grid gap-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-base">내 공고 목록</h3>
          <span className="text-xs text-gray-500">{myJobs.length}건</span>
        </div>

        {myJobs.length === 0 ? (
          <p className="text-sm text-gray-500">등록한 공고가 없습니다.</p>
        ) : (
          <ul className="divide-y">
            {myJobs.map((job) => (
              <li key={job.id} className="py-2 text-sm">
                <span className="font-medium">{job.title}</span> · {job.date} ·{' '}
                {job.time}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ✅ 내 공고 지원자 목록 */}
      <section className="border rounded-xl p-5 bg-white grid gap-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-base">지원자 현황</h3>
          <span className="text-xs text-gray-500">{myApplications.length}건</span>
        </div>

        {myApplications.length === 0 ? (
          <p className="text-sm text-gray-500">아직 지원자가 없습니다.</p>
        ) : (
          <ul className="divide-y">
            {myApplications.map((app) => (
              <li key={app.id} className="py-2 text-sm grid gap-1">
                <span className="font-medium">{app.pharmacist_name}</span>
                <span className="text-gray-600">{app.job_posts?.title}</span>
                <span className="text-xs text-gray-400">
                  지원일: {new Date(app.created_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>



    </div >
  )
}
